import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';

// Layout-level guard: blocks the route from rendering until Firebase Auth
// resolves and confirms the user is signed in. Pages keep their own auth
// effects for user-data loading; this one just gates the chrome.
//
// Auth enforcement at the API boundary (Cloud Functions verifying the
// Firebase ID token) remains the source of truth for security. This guard
// is a UX optimisation — it prevents a flash of authenticated chrome for
// unauth visitors, and it's why server-side session-cookie hardening
// (deferred item G in SECURITY-FOLLOWUPS-2026-05-09.md) is no longer
// needed once the migration cuts over.
export default function RequireAuth({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const location = useLocation();

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sage-700">
        Checking your session…
      </div>
    );
  }

  if (user === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
